import { memo } from 'react'
import { InputWithSuffix } from '@components/ui/input-group'
import { Slider } from '@components/ui/slider'
import { ToggleGroup, ToggleGroupItem } from '@components/ui/toggle-group'
import { cn } from '@/utils'
import { ROTATION_SLIDER_MAX_DEG } from '../constants/panel-limits'
import { handleBlurOnEnter } from '../handlers/blur-on-enter'

const rotationPresets = [0, 45, 90, 180, 270]

interface RotationControlProps {
  rotation: number[]
  rotationText: string
  error?: string
  disabled: boolean
  onPresetChange: (value: string) => void
  onSliderPointerDown: () => void
  onSliderPointerEnd: () => void
  onSliderValueChange: (value: number[]) => void
  onSliderValueCommit: (value: number[]) => void
  onTextFocus: () => void
  onTextChange: (raw: string) => void
  onTextCommit: (raw: string) => void
}

/** Rotate: preset angles, slider and degree text field. */
function RotationControl({
  rotation,
  rotationText,
  error,
  disabled,
  onPresetChange,
  onSliderPointerDown,
  onSliderPointerEnd,
  onSliderValueChange,
  onSliderValueCommit,
  onTextFocus,
  onTextChange,
  onTextCommit,
}: RotationControlProps) {
  return (
    <div className="space-y-2">
      <label className="text-sm font-medium text-foreground leading-none">Rotate</label>

      {/* Rotation Presets */}
      <ToggleGroup
        type="single"
        value={rotationPresets.includes(rotation[0]) ? rotation[0].toString() : ''}
        onValueChange={onPresetChange}
        className="justify-start gap-1"
        disabled={disabled}
      >
        {rotationPresets.map((angle, index) => (
          <ToggleGroupItem
            key={angle}
            value={angle.toString()}
            className={cn(
              'h-9 px-2 text-sm font-medium leading-normal',
              index === 0 ? 'w-[40px]' : 'min-w-[32px]'
            )}
          >
            {angle}°
          </ToggleGroupItem>
        ))}
      </ToggleGroup>

      {/* Rotation Slider */}
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-[18px]">
          <div
            className="flex-1 min-w-0 max-w-[130px]"
            onPointerDownCapture={onSliderPointerDown}
            onPointerUpCapture={onSliderPointerEnd}
            onPointerCancelCapture={onSliderPointerEnd}
          >
            <Slider
              value={rotation}
              onValueChange={onSliderValueChange}
              onValueCommit={onSliderValueCommit}
              max={ROTATION_SLIDER_MAX_DEG}
              step={1}
              className="w-full"
              disabled={disabled}
            />
          </div>
          <InputWithSuffix
            type="text"
            inputMode="decimal"
            aria-label="Rotation in degrees"
            value={rotationText}
            onFocus={onTextFocus}
            onChange={(e) => onTextChange(e.target.value)}
            onBlur={(e) => onTextCommit(e.target.value)}
            onKeyDown={handleBlurOnEnter}
            size="compact"
            suffix={
              <span className="text-xs font-medium text-muted-foreground leading-none pr-0.5">
                deg
              </span>
            }
            disabled={disabled}
            className={cn(error && 'border-destructive focus-visible:ring-destructive')}
          />
        </div>
        {error && <span className="text-xs text-destructive">{error}</span>}
      </div>
    </div>
  )
}

/** Memoized: the object panel re-renders on every frame of a move, and this section doesn't show position. */
const MemoRotationControl = memo(RotationControl)

export { MemoRotationControl as RotationControl }
